import { useState } from "react";
import { API, setAuthToken } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUser } from "@/contexts/UserContext";
import { ArrowLeft, Mail } from "lucide-react";

export default function Login({ setIsAuthenticated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const { refreshUser } = useUser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      setAuthToken(response.data.access_token);
      await refreshUser();
      setIsAuthenticated(true);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1670708758134-f239a3b7036c?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Background"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#0F172A] to-[#020617]"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-effect rounded-2xl p-8 border border-white/10 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-4">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">BannerSync</h1>
            <p className="text-muted-foreground">
              {showForgot ? "Reset your password" : "Sign in to your account"}
            </p>
          </div>

          {showForgot ? (
            forgotSent ? (
              <div className="space-y-6" data-testid="forgot-password-sent">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
                  <Mail className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-sm text-emerald-300 font-medium mb-1">Check your inbox</p>
                  <p className="text-xs text-gray-400">
                    If an account exists for <strong className="text-white">{forgotEmail}</strong>, you'll receive a password reset link shortly.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }}
                  className="w-full text-muted-foreground hover:text-primary"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="bg-secondary/50 border-white/10"
                  />
                  <p className="text-xs text-gray-500">We'll send a reset link to this email if an account exists.</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 rounded-full font-medium shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition-all"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-foreground"></div>
                      Sending...
                    </div>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setShowForgot(false); setForgotEmail(""); }}
                  className="w-full text-muted-foreground hover:text-primary"
                  data-testid="back-to-login-from-form-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </form>
            )
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" data-testid="email-label">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="bg-secondary/50 border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" data-testid="password-label">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      data-testid="forgot-password-link"
                      className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                    className="bg-secondary/50 border-white/10"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-button"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 rounded-full font-medium shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition-all"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-foreground"></div>
                      Processing...
                    </div>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
